import React, { useEffect, useState } from 'react';
import { Mail, Phone, Calendar, Users as UsersIcon, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { apiCall, fetchAdmin } from './_helpers';

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'responded', label: 'Responded', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'booked', label: 'Booked', color: 'bg-green-100 text-green-700' },
  { value: 'declined', label: 'Declined', color: 'bg-gray-200 text-gray-600' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const EventInquiriesManager = ({ onMessage }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const refetch = () => {
    setLoading(true);
    fetchAdmin('event-inquiries').then((d) => {
      setItems(Array.isArray(d) ? d : []);
      setLoading(false);
    });
  };

  useEffect(() => {
    refetch();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await apiCall('PATCH', `event-inquiries/${id}`, { status });
      onMessage('success', `Marked as ${status}`);
      refetch();
    } catch (e) {
      onMessage('error', e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await apiCall('DELETE', `event-inquiries/${id}`);
      onMessage('success', 'Deleted');
      refetch();
    } catch (e) {
      onMessage('error', e.message);
    }
  };

  if (loading) {
    return <p className="text-gray-500">Loading inquiries…</p>;
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-md text-center text-gray-500">
        No private event inquiries yet. Submissions from <code>/private-events</code> will appear here.
      </div>
    );
  }

  const counts = items.reduce((acc, i) => {
    acc[i.status || 'new'] = (acc[i.status || 'new'] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 shadow-md flex gap-4 flex-wrap text-sm">
        <span className="font-semibold">{items.length} total</span>
        {STATUSES.map((s) => (
          <span key={s.value} className={`px-3 py-1 rounded-full ${s.color}`}>
            {s.label}: {counts[s.value] || 0}
          </span>
        ))}
      </div>

      {items.map((i) => {
        const open = expanded[i._id];
        const status = i.status || 'new';
        const meta = STATUSES.find((s) => s.value === status) || STATUSES[0];
        return (
          <div
            key={i._id}
            className="bg-white rounded-xl shadow-md overflow-hidden"
            data-testid={`event-inquiry-${i._id}`}
          >
            <div className="p-4 flex flex-wrap items-center gap-4">
              <button
                onClick={() => setExpanded({ ...expanded, [i._id]: !open })}
                className="text-gray-500 hover:bg-gray-100 p-1 rounded"
              >
                {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold">{i.name}</p>
                <p className="text-sm text-gray-500">{fmtDate(i.submitted_at)}</p>
              </div>
              <a href={`mailto:${i.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                <Mail size={14} /> {i.email}
              </a>
              {i.phone && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <Phone size={14} /> {i.phone}
                </span>
              )}
              {i.event_date && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <Calendar size={14} /> {i.event_date}
                </span>
              )}
              {i.guest_count && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <UsersIcon size={14} /> {i.guest_count}
                </span>
              )}
              <select
                value={status}
                onChange={(e) => updateStatus(i._id, e.target.value)}
                className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer ${meta.color}`}
                data-testid={`status-select-${i._id}`}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                onClick={() => remove(i._id)}
                className="text-red-600 hover:bg-red-50 p-2 rounded"
                data-testid={`delete-inquiry-${i._id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
            {open && (
              <div className="px-6 pb-6 pt-2 border-t bg-gray-50 space-y-3 text-sm">
                {i.event_type && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Event Type</p>
                    <p className="font-medium">{i.event_type}</p>
                  </div>
                )}
                {i.message && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Message</p>
                    <p className="whitespace-pre-wrap leading-relaxed text-gray-800">{i.message}</p>
                  </div>
                )}
                <div className="pt-2">
                  <a
                    href={`mailto:${i.email}?subject=Re: Private event inquiry at Brick Lane`}
                    className="inline-block bg-[#C9A86C] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#B8956A]"
                  >
                    Reply to {i.name.split(' ')[0]}
                  </a>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EventInquiriesManager;
